import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { toSameOriginWorkflowUrl } from '@/utils/workflow-file-url';
import { isSafeFetchUrl } from '@/utils/url-utils';
import { refreshFileUrls } from '@/services/workflow/get-file';
import { getRunBySession } from '@/services/workflow/get-run-by-session';
import { useOrgStore } from '@/stores/org-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import {
  Citation,
  InputValue,
  NodeInputType,
  NodeCategory,
  OFFICE_DOC_DISPLAY_TYPES,
  OFFICE_DOC_DEFAULT_EXTENSION,
  FILE_FETCHABLE_DISPLAY_TYPES,
} from '@/types/node-types';
import { officeDisplayTypeForUrl } from '@/pages/workflow/helper/office-document-result';
import { CitationsList } from '@/pages/workflow/component/citations-list';
import VideoPlayer from '@/pages/workflow/component/video-player';
import {
  AudioPreview,
  extractAudioAlignment,
} from '@/pages/workflow/component/audio-preview';
import {
  TextDisplay,
  MarkdownDisplay,
  JsonDisplay,
  selectJsonDisplayData,
  ImageDisplay,
  FileDownloadDisplay,
  PowerPointDocumentDisplay,
  WordDocumentDisplay,
  ExcelDocumentDisplay,
} from '@/pages/workflow/component/display-components';
import { Button } from '@/components/ui/button';
import { X, Download, Copy, AlertTriangle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getIconComponent } from '../../helper/icon-mapping';
import { getNodeStyling } from '@/constants/node-styling';
import { NodeStatusEvent, ErrorInfo } from '@/types/workflow-types';
import { useNotification } from '@/components/notifications';
import { getCleanMarkdownData } from '@/pages/workflow/helper/markdown-utils';
import { ExternalApiErrorBanner } from '@/pages/workflow/component/external-api-error-banner';

interface PreviewPanelProps {
  result?: Record<string, InputValue> | null | undefined;
  reactFlowGeneratedId?: string;
  statusEvents?: NodeStatusEvent[];
  isLoading?: boolean;
  isVisible?: boolean;
  onClose?: () => void;
  icon?: string;
  displayName?: string;
  nodeType?: string;
}

// Interface for metadata structure
interface Metadata {
  character_count: number;
  line_count: number;
  word_count: number;
}

// Type guard to check if an object has the metadata structure
function isMetadata(obj: unknown): obj is Metadata {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    'character_count' in obj &&
    'line_count' in obj &&
    'word_count' in obj &&
    typeof (obj as Metadata).character_count === 'number' &&
    typeof (obj as Metadata).line_count === 'number' &&
    typeof (obj as Metadata).word_count === 'number'
  );
}

/**
 * Pull a structured ``ErrorInfo`` out of an arbitrary node result, if
 * present.  The Temporal runtime attaches it as
 * ``result.error_info`` (also nested under ``result.metadata``
 * sometimes when persisted via NodeResult).  We accept either; missing
 * fields disqualify the result so the caller falls back to the legacy
 * "Error: <string>" rendering.
 */
function extractErrorInfo(
  result: Record<string, InputValue> | null | undefined,
): ErrorInfo | null {
  if (!result || typeof result !== 'object') {
    return null;
  }
  const candidates: unknown[] = [
    (result as Record<string, unknown>).error_info,
    (result as Record<string, unknown>).errorInfo,
  ];
  // Some persisted node_result shapes nest error_info inside metadata.
  const metadata = (result as Record<string, unknown>).metadata;
  if (metadata && typeof metadata === 'object') {
    candidates.push((metadata as Record<string, unknown>).error_info);
  }
  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === 'object' &&
      'code' in candidate &&
      'category' in candidate &&
      'user_message' in candidate &&
      'technical_message' in candidate
    ) {
      const c = candidate as Record<string, unknown>;
      return {
        code: String(c.code),
        category: String(c.category),
        provider: String(c.provider ?? 'unknown'),
        retryable: Boolean(c.retryable),
        attempts_made: Number(c.attempts_made ?? 1),
        user_message: String(c.user_message),
        technical_message: String(c.technical_message),
        actionable_hint:
          c.actionable_hint == null ? null : String(c.actionable_hint),
        http_status: c.http_status == null ? null : Number(c.http_status),
        request_id: c.request_id == null ? null : String(c.request_id),
      };
    }
  }
  return null;
}

// Present on both live node_result events and persisted output_data blobs.
function extractCitations(
  result: Record<string, InputValue> | null | undefined,
): Citation[] | null {
  if (!result || typeof result !== 'object') {
    return null;
  }
  const metadata = (result as Record<string, unknown>).metadata;
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  const citations = (metadata as Record<string, unknown>).citations;
  if (!Array.isArray(citations) || citations.length === 0) {
    return null;
  }
  return citations.filter(
    (c): c is Citation => c != null && typeof c === 'object' && 'title' in c,
  );
}

const OFFICE_DOC_DISPLAY_COMPONENTS: Partial<
  Record<NodeInputType, React.FC<{ data: string; className?: string }>>
> = {
  [NodeInputType.PPT_DOC_DISPLAY]: PowerPointDocumentDisplay,
  [NodeInputType.WORD_DOC_DISPLAY]: WordDocumentDisplay,
  [NodeInputType.EXCEL_DOC_DISPLAY]: ExcelDocumentDisplay,
};

// Legacy past-run results for Office documents are sometimes persisted with
// only a `data` URL and no `display_modal` (the same shape used for plain
// markdown/text past runs). Sniff the file extension so those don't get
// rendered as markdown with a copy/download-markdown action bar.
const inferLegacyOfficeDisplayType = officeDisplayTypeForUrl;

/**
 * Fetches a file as a blob and downloads it same-origin so we never
 * navigate away from an unsaved workflow. Falls back to a new-tab download
 * if CORS blocks the fetch.
 */
async function downloadFileFromUrl(
  url: string,
  filename: string,
): Promise<{ usedFallback: boolean }> {
  if (!isSafeFetchUrl(url)) {
    // Deliberately not falling through to the new-tab fallback below --
    // that fallback exists to recover from CORS-blocked *fetches* of an
    // otherwise-trusted URL, and must never be reached for a URL that
    // failed the safety check, or it would just navigate there anyway.
    const error = new Error('Refusing to fetch an unsafe file URL');
    Sentry.captureException(error, {
      extra: { url, context: 'blob-download' },
    });
    throw error;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    return { usedFallback: false };
  } catch (fetchError) {
    console.warn(
      'Blob download failed, falling back to new-tab download:',
      fetchError,
    );
    Sentry.captureException(fetchError, {
      extra: { url, context: 'blob-download' },
    });
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { usedFallback: true };
  }
}

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const ResultPreviewPanel: React.FC<PreviewPanelProps> = ({
  result,
  reactFlowGeneratedId,
  isLoading = false,
  isVisible = false,
  onClose,
  icon,
  displayName,
  nodeType,
}) => {
  const nodeStyling = getNodeStyling(nodeType as NodeCategory);
  const markdownContainerRef = useRef<HTMLDivElement | null>(null);
  const notification = useNotification();
  const { currentOrg } = useOrgStore();
  const currentWorkflowId = useWorkflowStore((s) => s.currentWorkflow?.id);
  const currentSession = useWorkflowStore((s) => s.currentSession);
  const [isDownloadingAudio, setIsDownloadingAudio] = useState(false);
  const [isDownloadingOfficeDoc, setIsDownloadingOfficeDoc] = useState(false);
  const [isDownloadingMarkdown, setIsDownloadingMarkdown] = useState(false);
  const [isDownloadingFileBacked, setIsDownloadingFileBacked] = useState(false);

  const resultFiles = (result as Record<string, unknown> | null | undefined)
    ?.files;
  const resultFilesArr = Array.isArray(resultFiles) ? resultFiles : [];

  // Set by a producing node when `files` carries the *same* text that was
  // stripped from the inline value (see the markdown output node). Without it,
  // `files` are unrelated artifacts — an agent node, for instance, always
  // renders as markdown while attaching its sandbox output files — and must
  // never be served as the markdown download.
  const resultMetadata = (result as Record<string, unknown> | null | undefined)
    ?.metadata;
  const contentOffloadedToFiles =
    resultMetadata != null &&
    typeof resultMetadata === 'object' &&
    (resultMetadata as Record<string, unknown>).content_offloaded_to_files ===
      true;

  // Set by the editor when the *displayed* string hit the WS delivery cap.
  // Nodes that don't offload to a file (an agent node, say) simply arrive
  // clipped, so the inline value is not safe to hand to a download.
  const wasClipped =
    (result as Record<string, unknown> | null | undefined)
      ?._content_truncated === true;

  // When a large text/JSON/markdown result has its inline value nulled by the
  // WS shaper (the node uploaded the full content to S3 and set
  // CONTENT_OFFLOADED_TO_FILES_KEY), fetch the full text through the backend
  // proxy and render it completely.
  const needsFileFetch =
    result?.display_modal != null &&
    FILE_FETCHABLE_DISPLAY_TYPES.has(result.display_modal as NodeInputType) &&
    result?.value == null &&
    resultFilesArr.length > 0;

  const [fetchedFileContent, setFetchedFileContent] = useState<string | null>(
    null,
  );
  const [fetchFileError, setFetchFileError] = useState(false);

  useEffect(() => {
    if (!needsFileFetch) {
      setFetchedFileContent(null);
      setFetchFileError(false);
      return;
    }

    let cancelled = false;
    setFetchedFileContent(null);
    setFetchFileError(false);

    const firstFile = resultFilesArr[0] as Record<string, unknown>;

    const doFetch = async () => {
      try {
        // Refresh through the backend so we get a same-origin proxy URL
        // instead of a raw S3 presigned URL (which browser fetch() blocks
        // due to CORS on non-allowlisted hosts such as Vercel PR previews).
        // A refresh failure isn't fatal -- fall back to whichever URL the
        // file already carries, same as before this refresh step existed.
        let fetchUrl = '';
        if (currentOrg?.orgid && firstFile?.s3_bucket && firstFile?.s3_key) {
          try {
            const [fresh] = await refreshFileUrls(
              currentOrg.orgid,
              // The WS event NodeFile has the same S3 fields refreshFileUrls
              // needs; cast through unknown to satisfy the stricter NodeFile type.
              [
                firstFile as unknown as Parameters<
                  typeof refreshFileUrls
                >[1][number],
              ],
            );
            const rawUrl =
              (fresh?.presigned_url as string | undefined) ||
              (fresh?.download_url as string | undefined) ||
              '';
            fetchUrl = toSameOriginWorkflowUrl(rawUrl);
          } catch (refreshErr) {
            Sentry.captureException(refreshErr, {
              extra: { context: 'preview-panel-file-refresh' },
            });
          }
        }

        if (!fetchUrl) {
          const rawUrl =
            (firstFile?.presigned_url as string | undefined) ||
            (firstFile?.download_url as string | undefined) ||
            '';
          fetchUrl = toSameOriginWorkflowUrl(rawUrl);
        }

        if (!fetchUrl) throw new Error('No fetchable URL for file');
        if (!isSafeFetchUrl(fetchUrl)) {
          throw new Error('Refusing to fetch an unsafe file URL');
        }

        const res = await fetch(fetchUrl);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const text = await res.text();
        if (!cancelled) {
          setFetchedFileContent(text);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          Sentry.captureException(err, {
            extra: { context: 'preview-panel-file-fetch' },
          });
          setFetchFileError(true);
        }
      }
    };

    void doFetch();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFileFetch, currentOrg?.orgid, JSON.stringify(resultFilesArr)]);

  const handleCopyFetchedContent = useCallback(async () => {
    if (fetchedFileContent == null) return;
    try {
      await navigator.clipboard.writeText(fetchedFileContent);
      notification.success('Content copied to clipboard');
    } catch {
      notification.error('Failed to copy content');
    }
  }, [fetchedFileContent, notification]);

  // Check if we're displaying markdown.  Two shapes are supported:
  // 1. Live execution / post-fix past runs: display_modal === MARKDOWN with `value`
  // 2. Legacy past runs (persisted without display_modal): `data` is a string
  //
  // Important: post-fix past runs for non-markdown types (e.g. audio) also
  // persist a string `data` field alongside `display_modal`. Only treat
  // string `data` as markdown when there is no display_modal, otherwise
  // audio past runs incorrectly show Copy Text + Download.
  //
  // Legacy Office-document past runs (PPT/Word/Excel) can also lack
  // display_modal, leaving only a `data` URL indistinguishable from a
  // legacy markdown/text result — sniff the extension so those don't get
  // rendered as markdown with a copy/download-markdown action bar either.
  const legacyOfficeDisplayType =
    !result?.display_modal && typeof result?.data === 'string'
      ? inferLegacyOfficeDisplayType(result.data)
      : null;

  // Also true when value is null but files are present — the Download button
  // must still be accessible even when inline content was not delivered.
  const isMarkdownFromDisplayModal =
    result?.display_modal === NodeInputType.MARKDOWN &&
    (result?.value != null || resultFilesArr.length > 0);
  const isMarkdownFromData =
    !result?.display_modal &&
    result?.data != null &&
    typeof result.data === 'string' &&
    !legacyOfficeDisplayType;
  const isMarkdown = isMarkdownFromDisplayModal || isMarkdownFromData;

  const isAudio =
    result?.display_modal === NodeInputType.AUDIO &&
    ((typeof result?.value === 'string' && result.value !== '') ||
      (typeof result?.data === 'string' && result.data !== ''));

  // Covers both the live/post-fix shape (display_modal is one of the
  // Office display types) and the legacy shape sniffed above (no
  // display_modal, extension inferred from the `data` URL).
  const officeDocDisplayType =
    (result?.display_modal &&
    OFFICE_DOC_DISPLAY_TYPES.has(result.display_modal as NodeInputType)
      ? (result.display_modal as NodeInputType)
      : null) ?? legacyOfficeDisplayType;
  const isOfficeDoc = officeDocDisplayType != null;

  const getAudioUrl = (): string | null => {
    if (!isAudio) return null;
    if (typeof result?.value === 'string' && result.value) {
      return result.value;
    }
    if (typeof result?.data === 'string' && result.data) {
      return result.data;
    }
    return null;
  };

  const getAudioFilename = (): string => {
    const files = (result as Record<string, unknown> | null | undefined)?.files;
    if (Array.isArray(files) && files.length > 0) {
      const name = (files[0] as Record<string, unknown>)?.name;
      if (typeof name === 'string' && name) {
        return name;
      }
    }

    const audioUrl = getAudioUrl();
    if (audioUrl) {
      try {
        const basename = new URL(audioUrl).pathname.split('/').pop();
        if (basename && /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(basename)) {
          return decodeURIComponent(basename);
        }
      } catch {
        // Keep default
      }
    }
    return 'audio.mp3';
  };

  const getOfficeDocUrl = (): string | null => {
    if (!isOfficeDoc) return null;
    if (typeof result?.value === 'string' && result.value) {
      return result.value;
    }
    if (typeof result?.data === 'string' && result.data) {
      return result.data;
    }
    return null;
  };

  const getOfficeDocFilename = (): string => {
    const files = (result as Record<string, unknown> | null | undefined)?.files;
    if (Array.isArray(files) && files.length > 0) {
      const name = (files[0] as Record<string, unknown>)?.name;
      if (typeof name === 'string' && name) {
        return name;
      }
    }

    const docUrl = getOfficeDocUrl();
    if (docUrl) {
      try {
        const basename = new URL(docUrl).pathname.split('/').pop();
        if (basename && /\.(pptx?|docx?|xlsx?)$/i.test(basename)) {
          return decodeURIComponent(basename);
        }
      } catch {
        // Keep default
      }
    }

    const extension = officeDocDisplayType
      ? OFFICE_DOC_DEFAULT_EXTENSION[officeDocDisplayType]
      : undefined;
    return `document.${extension ?? 'pptx'}`;
  };

  const getMarkdownContent = (): string => {
    if (isMarkdownFromDisplayModal) {
      if (result.value == null) return '';
      return getCleanMarkdownData(String(result.value));
    }
    if (isMarkdownFromData) {
      return getCleanMarkdownData(String(result.data));
    }
    return '';
  };

  /**
   * Fetch this node's full output from the persisted run row.
   *
   * The WebSocket shaper caps every string it sends to the browser, but the
   * REST run endpoints are never shaped — `node_result.output_data` holds the
   * complete text. Returns null when the run/node can't be resolved or the
   * persisted row has nothing longer than what we already have.
   */
  const fetchFullPersistedContent = useCallback(async (): Promise<
    string | null
  > => {
    if (!currentWorkflowId || !currentSession || !currentOrg?.orgid)
      return null;
    if (!reactFlowGeneratedId) return null;

    try {
      const { data } = await getRunBySession(
        currentWorkflowId,
        currentSession,
        currentOrg.orgid,
      );
      const persisted = data?.node_results?.find(
        (nodeResult) => nodeResult.node_instance_id === reactFlowGeneratedId,
      )?.output_data;
      if (!persisted) return null;

      // `output_data` merges the display-model envelope (`value`) over the
      // node's raw output (`data`); either may carry the full text.
      const candidate =
        typeof persisted.value === 'string'
          ? persisted.value
          : typeof persisted.data === 'string'
            ? persisted.data
            : null;
      return candidate || null;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'preview-panel-full-content-refetch' },
      });
      return null;
    }
  }, [
    currentWorkflowId,
    currentSession,
    currentOrg?.orgid,
    reactFlowGeneratedId,
  ]);

  // `files[0]` holds the previewed content only when the producing node
  // offloaded it there, or when there's nothing else to download anyway
  // (value null, nothing fetched yet). Without this gate, `files` can be
  // unrelated artifacts — an agent node, for instance, always renders as
  // markdown while attaching its sandbox output files — and must never be
  // served as the download.
  const fileBackedResultFile =
    resultFilesArr.length > 0 &&
    (contentOffloadedToFiles ||
      (result?.value == null && fetchedFileContent == null))
      ? (resultFilesArr[0] as Record<string, unknown>)
      : null;

  // Shared four-branch download ladder for file-backed results (Markdown,
  // TEXT_OUTPUT, JSON): reuse in-memory content already fetched for the
  // preview, fall back to the file's own URL, then to a persisted-run
  // refetch (or the clipped inline value) once nothing is file-backed.
  // `resultFilesArr` is a fresh array on every render, so track its content
  // instead (matches the file-fetch effect above).
  const resultFilesKey = JSON.stringify(resultFilesArr);
  const downloadFileBackedResult = useCallback(
    async ({
      gatedFile,
      mimeType,
      defaultFileName,
      label,
      getFallbackContent,
      setIsDownloading,
    }: {
      gatedFile: Record<string, unknown> | null;
      mimeType: string;
      defaultFileName: string;
      label: string;
      getFallbackContent: () => string;
      setIsDownloading: (value: boolean) => void;
    }) => {
      // Happy path A: full content already fetched into memory for the
      // preview. The fetch effect only ever reads files[0], so that's always
      // the right source for the filename here, independent of the gate.
      if (fetchedFileContent != null) {
        const fetchedSource = resultFilesArr[0] as
          | Record<string, unknown>
          | undefined;
        downloadBlob(
          fetchedFileContent,
          (fetchedSource?.name as string | undefined) || defaultFileName,
          mimeType,
        );
        return;
      }

      const fileName =
        (gatedFile?.name as string | undefined) || defaultFileName;

      // Happy path B: file URL available (offloaded to a file but not yet
      // fetched).
      const fileUrl =
        (gatedFile?.presigned_url as string | undefined) ||
        (gatedFile?.download_url as string | undefined);
      if (fileUrl) {
        setIsDownloading(true);
        try {
          // Must go through the blob helper: a cross-origin `<a download>`
          // has its download attribute ignored by the browser, turning the
          // click into a top-level navigation away from the workflow.
          const { usedFallback } = await downloadFileFromUrl(
            toSameOriginWorkflowUrl(fileUrl),
            fileName,
          );
          notification.success(
            usedFallback
              ? 'Download started'
              : `${label} downloaded successfully`,
          );
        } catch (error) {
          console.error(`Failed to download ${label.toLowerCase()}:`, error);
          Sentry.captureException(error, {
            extra: { fileUrl, context: 'file-backed-download' },
          });
          notification.error(`Failed to download ${label.toLowerCase()}`);
        } finally {
          setIsDownloading(false);
        }
        return;
      }

      if (gatedFile && result?.value == null) {
        // Expected file-backed content but the file has no usable URL, and
        // there's no inline value to fall back on either -- there is
        // nothing to download. Don't fall through to the inline fallback
        // below, which would silently write an empty file and report
        // success. When the file is trusted but an inline value is still
        // present (e.g. a past run served over REST), fall through instead.
        notification.error(
          `Failed to download ${label.toLowerCase()}: content unavailable`,
        );
        return;
      }

      // The inline value is all that's left, and it may have been clipped
      // on the way to the browser. Pull the unshaped text off the persisted
      // run row so the file on disk isn't a truncated copy of the preview.
      setIsDownloading(true);
      try {
        const fullContent = wasClipped
          ? await fetchFullPersistedContent()
          : null;
        const content = fullContent ?? getFallbackContent();
        if (!content) {
          notification.error(
            `Failed to download ${label.toLowerCase()}: content unavailable`,
          );
          return;
        }
        downloadBlob(content, defaultFileName, mimeType);
        if (wasClipped && fullContent == null) {
          notification.warning(
            'Downloaded the previewed excerpt — the full output could not be ' +
              'retrieved from this run.',
          );
        } else {
          notification.success(`${label} downloaded successfully`);
        }
      } catch (error) {
        console.error(`Failed to download ${label.toLowerCase()}:`, error);
        Sentry.captureException(error, {
          extra: { context: 'file-backed-download-fallback' },
        });
        notification.error(`Failed to download ${label.toLowerCase()}`);
      } finally {
        setIsDownloading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchedFileContent,
      resultFilesKey,
      result?.value,
      wasClipped,
      fetchFullPersistedContent,
      notification,
    ],
  );

  const handleDownloadMarkdown = async () => {
    if (!isMarkdown || isDownloadingMarkdown) return;
    await downloadFileBackedResult({
      gatedFile: fileBackedResultFile,
      mimeType: 'text/markdown',
      defaultFileName: 'markdown-content.md',
      label: 'Markdown file',
      getFallbackContent: getMarkdownContent,
      setIsDownloading: setIsDownloadingMarkdown,
    });
  };

  // True when a text/JSON node has a downloadable result — either the happy
  // path (value offloaded to a file, value null + files present) or the
  // fail-open path (offload failed, content was clipped inline).
  const isTextOrJsonModal =
    result?.display_modal === NodeInputType.TEXT_OUTPUT ||
    result?.display_modal === NodeInputType.JSON;
  const hasTextOrJsonDownload =
    isTextOrJsonModal &&
    (result?.value == null
      ? resultFilesArr.length > 0 // offloaded to a file
      : wasClipped); // fail-open: offload failed, content was clipped

  const handleDownloadFileBacked = async () => {
    if (!hasTextOrJsonDownload || isDownloadingFileBacked) return;
    const isJson = result?.display_modal === NodeInputType.JSON;
    await downloadFileBackedResult({
      gatedFile: fileBackedResultFile,
      mimeType: isJson ? 'application/json' : 'text/plain',
      defaultFileName: isJson ? 'output.json' : 'output.txt',
      label: 'File',
      getFallbackContent: () =>
        typeof result?.value === 'string' ? result.value : '',
      setIsDownloading: setIsDownloadingFileBacked,
    });
  };

  const handleDownloadAudio = async () => {
    if (!isAudio || isDownloadingAudio) return;

    const audioUrl = getAudioUrl();
    if (!audioUrl) return;

    setIsDownloadingAudio(true);
    try {
      const filename = getAudioFilename();
      const { usedFallback } = await downloadFileFromUrl(audioUrl, filename);
      notification.success(
        usedFallback
          ? 'Audio download started'
          : 'Audio file downloaded successfully',
      );
    } catch (error) {
      console.error('Failed to download audio:', error);
      Sentry.captureException(error, {
        extra: { audioUrl, context: 'audio-download' },
      });
      notification.error('Failed to download audio file');
    } finally {
      setIsDownloadingAudio(false);
    }
  };

  const handleDownloadOfficeDoc = async () => {
    if (!isOfficeDoc || isDownloadingOfficeDoc) return;

    const docUrl = getOfficeDocUrl();
    if (!docUrl) return;

    setIsDownloadingOfficeDoc(true);
    try {
      const filename = getOfficeDocFilename();
      const { usedFallback } = await downloadFileFromUrl(docUrl, filename);
      notification.success(
        usedFallback ? 'Download started' : 'File downloaded successfully',
      );
    } catch (error) {
      console.error('Failed to download file:', error);
      Sentry.captureException(error, {
        extra: { docUrl, context: 'office-doc-download' },
      });
      notification.error('Failed to download file');
    } finally {
      setIsDownloadingOfficeDoc(false);
    }
  };

  const handleCopyMarkdown = async () => {
    if (!isMarkdown) return;

    try {
      const cleanData = getMarkdownContent();
      await navigator.clipboard.writeText(cleanData);
      notification.success('Raw markdown text copied to clipboard');
    } catch (error) {
      console.error('Failed to copy markdown:', error);
      notification.error('Failed to copy markdown to clipboard');
    }
  };

  const handleCopyHTML = async () => {
    if (!isMarkdown || !markdownContainerRef.current) {
      notification.error('Failed to copy HTML: content not available');
      return;
    }

    try {
      const markdownDiv = markdownContainerRef.current
        .firstElementChild as HTMLElement;
      if (!markdownDiv) {
        notification.error('Failed to copy HTML: rendered content not found');
        return;
      }

      const htmlContent = markdownDiv.innerHTML;
      const cleanData = getMarkdownContent();

      if (navigator.clipboard && navigator.clipboard.write) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([cleanData], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        await navigator.clipboard.writeText(htmlContent);
      }

      notification.success('Formatted text copied to clipboard');
    } catch (error) {
      console.error('Failed to copy HTML:', error);
      notification.error('Failed to copy HTML to clipboard');
    }
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  const loadingState = () => {
    const getLoadingText = () => {
      if (isLoading) return 'Loading...';
      if (
        result &&
        'type' in result &&
        (result as Record<string, unknown>).type === 'error'
      )
        return 'Error';
      if (result) return 'Processing...';
      return 'No result';
    };

    return (
      <div className='flex items-center justify-center py-8 text-muted-foreground'>
        <div className='flex items-center gap-2'>
          <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500'></div>
          <span>{getLoadingText()}</span>
        </div>
      </div>
    );
  };
  // `hasDownloadButton` reflects whether an actual Download button is on
  // screen for this result — it isn't when there's neither a fetchable file
  // nor an inline value, so the copy shouldn't point users at a button that
  // doesn't exist.
  const fetchErrorBanner = (hasDownloadButton: boolean) => (
    <div className='max-w-4xl'>
      <div className='flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'>
        <AlertTriangle className='h-4 w-4 shrink-0' />
        {hasDownloadButton
          ? 'Could not load preview. Use the Download button above to access the full file.'
          : 'Could not load preview, and no file is available to download.'}
      </div>
    </div>
  );

  // Shared render for the "offloaded to a file" branch of a file-fetchable
  // modal: spin while the fetch is in flight, show a failure banner once it
  // isn't possible to wait any longer, or hand the fetched text to the
  // caller's display component.
  const renderOffloadedContent = (
    hasDownloadButton: boolean,
    render: (content: string) => React.ReactNode,
  ): React.ReactNode => {
    if (fetchedFileContent == null) {
      // Only spin while a fetch is actually possible and hasn't failed yet
      // -- otherwise (no files to fetch from, or the fetch already errored)
      // there's nothing to wait for, so show the failure banner instead of
      // spinning forever.
      if (needsFileFetch && !fetchFileError) {
        return loadingState();
      }
      return fetchErrorBanner(hasDownloadButton);
    }
    return render(fetchedFileContent);
  };

  // Function to render the actual result content
  const renderResultContent = (): React.ReactNode => {
    // Handle error results.
    //
    // Two shapes are supported:
    // 1. Legacy: ``{ type: 'error', message: string }`` -- shown as a
    //    plain red box.  Kept for backward compatibility with any
    //    code path that still surfaces errors that way.
    // 2. Structured: ``{ ..., error_info: ErrorInfo }`` from the
    //    Temporal-backed runtime -- routed through
    //    ``ExternalApiErrorBanner`` so the user can immediately see
    //    whether it's an external-service problem (Anthropic /
    //    OpenAI), a configuration problem (bad key, exhausted quota),
    //    a user-input problem (content policy, context length), or
    //    something internal we should look at.
    const errorInfo = extractErrorInfo(result);
    if (errorInfo) {
      return (
        <div className='max-w-4xl'>
          <ExternalApiErrorBanner info={errorInfo} />
        </div>
      );
    }

    if (result && result.type === 'error') {
      return (
        <div className='text-red-600 p-4 bg-red-50 rounded-md'>
          Error: {String(result.message)}
        </div>
      );
    }

    // The caller (e.g. artifact-node's direct file fetch) may set isLoading
    // with a null value while content is in flight. Show a spinner instead
    // of falling through to render `value` as literal "null". Gated on a
    // null value so this doesn't blank an already-available live result
    // while the surrounding workflow execution is still marked loading.
    if (isLoading && result?.value == null) {
      return loadingState();
    }

    // Handle NodeParam results - check if it has display_modal
    if (result && result.display_modal && result.value !== undefined) {
      const displayType = result.display_modal as NodeInputType;
      // Properly handle objects/arrays to avoid [object Object] output
      const value =
        typeof result.value === 'object' && result.value !== null
          ? JSON.stringify(result.value, null, 2)
          : String(result.value);
      // Check if the value is a JSON object/array for proper display routing
      const isJsonValue =
        typeof result.value === 'object' && result.value !== null;

      const hasFullFileRef = resultFilesArr.length > 0;
      const truncationBanner = wasClipped ? (
        <div className='mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'>
          <AlertTriangle className='h-4 w-4 shrink-0' />
          {hasFullFileRef
            ? 'Output exceeds the preview limit — only the first portion is shown. Use the Download button above to get the complete file.'
            : 'Content was truncated during live delivery. Reload the run to see the full output.'}
        </div>
      ) : null;

      switch (displayType) {
        case NodeInputType.FILE_BROWSER_DISPLAY: {
          // Handle array of file objects with name and presigned_url
          if (isJsonValue && Array.isArray(result.value)) {
            const files = result.value as unknown as Array<
              Record<string, unknown>
            >;
            // Build markdown string matching the backend format
            const markdown = [
              ...files.map((file, index) => {
                const name = String(file.name || 'Unnamed file');
                const url = String(file.presigned_url || '');
                return url
                  ? `**${index + 1}. ${name}** 🔗 [${url}](${url})`
                  : `**${index + 1}. ${name}**`;
              }),
            ].join('\n');

            return (
              <div className='max-w-4xl'>
                <MarkdownDisplay data={markdown} />
              </div>
            );
          }

          // If value is already a markdown string, render directly
          if (typeof result.value === 'string') {
            return (
              <div className='max-w-4xl'>
                <MarkdownDisplay data={value} />
              </div>
            );
          }

          // Fallback to JSON display
          return (
            <div className='max-w-4xl'>
              <JsonDisplay data={value} />
            </div>
          );
        }

        case NodeInputType.TEXT_OUTPUT:
          if (result.value == null) {
            return renderOffloadedContent(hasTextOrJsonDownload, (content) => (
              <div className='max-w-4xl'>
                <TextDisplay data={content} />
              </div>
            ));
          }
          // If the value is an object/array, display as JSON instead of plain text
          if (isJsonValue) {
            return (
              <div className='max-w-4xl'>
                {truncationBanner}
                <JsonDisplay data={value} />
              </div>
            );
          }
          return (
            <div className='max-w-4xl'>
              {truncationBanner}
              <TextDisplay data={value} />
            </div>
          );

        case NodeInputType.MARKDOWN:
          if (result.value == null) {
            return renderOffloadedContent(isMarkdown, (content) => (
              <div className='max-w-4xl'>
                <MarkdownDisplay
                  data={content}
                  className='pb-4'
                  onContainerRef={(ref) => {
                    markdownContainerRef.current = ref;
                  }}
                />
                <CitationsList citations={extractCitations(result)} />
              </div>
            ));
          }
          return (
            <div className='max-w-4xl'>
              {truncationBanner}
              <MarkdownDisplay
                data={value}
                className='pb-4'
                onContainerRef={(ref) => {
                  markdownContainerRef.current = ref;
                }}
              />
              <CitationsList citations={extractCitations(result)} />
            </div>
          );

        case NodeInputType.JSON: {
          if (result.value == null) {
            return renderOffloadedContent(hasTextOrJsonDownload, (content) => (
              <div className='max-w-4xl'>
                <JsonDisplay data={content} />
              </div>
            ));
          }
          const displayData = selectJsonDisplayData(
            result.value,
            (result as Record<string, unknown>).data,
          );
          return (
            <div className='max-w-4xl'>
              {truncationBanner}
              <JsonDisplay data={displayData} />
            </div>
          );
        }

        case NodeInputType.IMAGE:
          return (
            <div className='max-w-4xl'>
              <ImageDisplay data={value} />
            </div>
          );

        case NodeInputType.VIDEO:
          return <VideoPlayer videoDataUrl={value} />;

        case NodeInputType.AUDIO:
          return (
            <AudioPreview
              audioUrl={value}
              alignment={extractAudioAlignment(
                result as Record<string, unknown>,
              )}
            />
          );

        case NodeInputType.FILE_DOWNLOAD:
          return (
            <div className='max-w-4xl'>
              <FileDownloadDisplay data={value} />
            </div>
          );

        case NodeInputType.PPT_DOC_DISPLAY:
          return (
            <div className='w-full h-full min-h-[600px]'>
              <PowerPointDocumentDisplay data={value} />
            </div>
          );

        case NodeInputType.WORD_DOC_DISPLAY:
          return (
            <div className='w-full h-full min-h-[600px]'>
              <WordDocumentDisplay data={value} />
            </div>
          );

        case NodeInputType.EXCEL_DOC_DISPLAY:
          return (
            <div className='w-full h-full min-h-[600px]'>
              <ExcelDocumentDisplay data={value} />
            </div>
          );

        default:
          // If the value is an object/array, display as JSON instead of plain text
          if (isJsonValue) {
            return (
              <div className='max-w-4xl'>
                <JsonDisplay data={value} />
              </div>
            );
          }
          return (
            <div className='max-w-4xl'>
              <TextDisplay data={value} />
            </div>
          );
      }
    }

    if (isAudio) {
      const audioUrl = getAudioUrl();
      if (audioUrl) {
        return (
          <AudioPreview
            audioUrl={audioUrl}
            alignment={extractAudioAlignment(result as Record<string, unknown>)}
          />
        );
      }
    }

    if (legacyOfficeDisplayType && typeof result?.data === 'string') {
      const OfficeDisplay =
        OFFICE_DOC_DISPLAY_COMPONENTS[legacyOfficeDisplayType];
      if (OfficeDisplay) {
        return (
          <div className='w-full h-full min-h-[600px]'>
            <OfficeDisplay data={result.data} />
          </div>
        );
      }
    }

    // Handle the new result structure with data field (e.g. past-run
    // results persisted without display_modal).  Render as markdown so
    // the output looks identical to the live-execution experience.
    //
    // Guard on !result.display_modal: post-fix past runs for non-markdown
    // types (e.g. audio) also persist a string `data` field alongside
    // display_modal, so without this check they'd render as markdown text.
    if (
      result &&
      !result.display_modal &&
      result.data != null &&
      typeof result.data === 'string'
    ) {
      const metadata = result.metadata;
      const hasValidMetadata = isMetadata(metadata);

      return (
        <div className='max-w-4xl'>
          <MarkdownDisplay
            data={String(result.data)}
            className='pb-4'
            onContainerRef={(ref) => {
              markdownContainerRef.current = ref;
            }}
          />
          <CitationsList citations={extractCitations(result)} />
          {hasValidMetadata && (
            <div className='mt-4 text-sm text-muted-foreground'>
              <span>Characters: {metadata.character_count}</span>
              <span className='mx-2'>•</span>
              <span>Lines: {metadata.line_count}</span>
              <span className='mx-2'>•</span>
              <span>Words: {metadata.word_count}</span>
            </div>
          )}
        </div>
      );
    }

    // Generic fallback for any other result structure
    if (result) {
      return (
        <div className='max-w-4xl'>
          {typeof result === 'object' ? (
            <JsonDisplay data={JSON.stringify(result, null, 2)} />
          ) : (
            <TextDisplay data={String(result)} />
          )}
        </div>
      );
    }

    return loadingState();
  };

  return (
    <div
      className='w-full max-h-full bg-popover border border-border shadow-lg rounded-lg flex flex-col min-w-0 dark:bg-popover'
      onClick={(e) => e.stopPropagation()}
    >
      {/* Panel Header */}
      <div className='flex items-center justify-between p-4 border-b border-border'>
        <div className='flex items-center gap-2 text-sm font-semibold w-full'>
          <span style={{ color: nodeStyling.primaryColor }}>
            {getIconComponent(icon || '', nodeStyling.primaryColor)}
          </span>
          <span>{displayName}</span>
        </div>
        <div className='flex items-center gap-2'>
          {isMarkdown && (
            <>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => {
                  void handleDownloadMarkdown();
                }}
                disabled={isDownloadingMarkdown}
                className='h-8 flex items-center gap-2'
                aria-label='Download markdown file'
              >
                <Download className='h-4 w-4' />
                {isDownloadingMarkdown ? 'Downloading…' : 'Download'}
              </Button>
              {/* Show Copy when inline content exists or fetch succeeded */}
              {(result?.value != null || fetchedFileContent != null) &&
                (fetchedFileContent != null && result?.value == null ? (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      void handleCopyFetchedContent();
                    }}
                    className='h-8 flex items-center gap-2'
                    aria-label='Copy content'
                  >
                    <Copy className='h-4 w-4' />
                    Copy Text
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-8 flex items-center gap-2'
                        aria-label='Copy options'
                      >
                        <Copy className='h-4 w-4' />
                        Copy Text
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem onClick={handleCopyMarkdown}>
                        <Copy className='h-4 w-4 mr-2' />
                        Copy as Markdown
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleCopyHTML}>
                        <Copy className='h-4 w-4 mr-2' />
                        Copy as Formatted Text
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ))}
            </>
          )}
          {hasTextOrJsonDownload && (
            <>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => {
                  void handleDownloadFileBacked();
                }}
                disabled={isDownloadingFileBacked}
                className='h-8 flex items-center gap-2'
                aria-label='Download file'
              >
                <Download className='h-4 w-4' />
                {isDownloadingFileBacked ? 'Downloading…' : 'Download'}
              </Button>
              {fetchedFileContent != null && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    void handleCopyFetchedContent();
                  }}
                  className='h-8 flex items-center gap-2'
                  aria-label='Copy content'
                >
                  <Copy className='h-4 w-4' />
                  Copy Text
                </Button>
              )}
            </>
          )}
          {isAudio && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                void handleDownloadAudio();
              }}
              disabled={isDownloadingAudio}
              className='h-8 flex items-center gap-2'
              aria-label='Download audio file'
            >
              <Download className='size-4' />
              {isDownloadingAudio ? 'Downloading…' : 'Download'}
            </Button>
          )}
          {isOfficeDoc && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                void handleDownloadOfficeDoc();
              }}
              disabled={isDownloadingOfficeDoc}
              className='h-8 flex items-center gap-2'
              aria-label='Download file'
            >
              <Download className='size-4' />
              {isDownloadingOfficeDoc ? 'Downloading…' : 'Download'}
            </Button>
          )}
          <Button
            variant='ghost'
            size='sm'
            onClick={onClose}
            className='h-8 w-8 p-0'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      </div>

      {/* Panel Content */}
      <div className='flex-1 flex flex-col overflow-hidden min-h-0 max-h-full'>
        <div
          className='flex-1 p-4 bg-muted-background min-h-0 max-h-full overflow-auto'
          data-preview-panel='true'
        >
          {renderResultContent()}
        </div>
      </div>
    </div>
  );
};
